import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Descriptions, Empty, List, Modal, Space, Spin, Table, Tag, Typography } from "antd";
import enhancedApi from "../../../../api/enhancedApi";

const { Text } = Typography;

const formatDateRange = (range) => {
    if (!range?.start && !range?.end) return "无航班日期";
    if (range.start === range.end) return range.start;
    return `${range.start || "?"} ~ ${range.end || "?"}`;
};

const formatAircraftRegs = (regs) => {
    if (!Array.isArray(regs) || regs.length === 0) return "未识别飞机";
    return regs.join(", ");
};

const formatBlockingReasons = (reasons) => {
    if (!Array.isArray(reasons) || reasons.length === 0) return "";
    return reasons.map((reason) => reason.message || reason.code).filter(Boolean).join("；");
};

const extractApiErrorMessage = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((item) => item?.msg || item?.message || String(item)).join("；");
    if (detail?.message) return detail.message;
    if (error?.message) return error.message;
    return fallback;
};

const V2TrackImportModal = ({ open, schedule, onCancel, onImportRows }) => {
    const [loading, setLoading] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [candidates, setCandidates] = useState([]);
    const [targetSchedule, setTargetSchedule] = useState(null);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState("");
    const [previewError, setPreviewError] = useState("");

    const scheduleId = schedule?.id;

    const loadCandidates = useCallback(async () => {
        if (!open || !scheduleId) return;
        setLoading(true);
        setError("");
        setCandidates([]);
        setSelectedCandidate(null);
        setPreview(null);
        setPreviewError("");

        try {
            const response = await enhancedApi.schedule.getV2TrackImportSources(scheduleId);
            setCandidates(response?.data?.items || []);
            setTargetSchedule(response?.data?.target_schedule || null);
        } catch (loadError) {
            setError(extractApiErrorMessage(loadError, "获取 V2 编辑轨来源失败，请确认权限或稍后重试。"));
        } finally {
            setLoading(false);
        }
    }, [open, scheduleId]);

    useEffect(() => {
        loadCandidates();
    }, [loadCandidates]);

    const handleSelectCandidate = useCallback(
        async (candidate) => {
            if (!scheduleId || !candidate?.track?.id) return;
            setSelectedCandidate(candidate);
            setPreview(null);
            setPreviewError("");
            setPreviewLoading(true);

            try {
                const response = await enhancedApi.schedule.previewV2TrackImport(scheduleId, {
                    draft_id: candidate.draft?.id,
                    track_id: candidate.track.id,
                });
                setPreview(response?.data || null);
            } catch (previewLoadError) {
                setPreviewError(extractApiErrorMessage(previewLoadError, "预览 V2 编辑轨失败，请确认来源仍可读取。"));
            } finally {
                setPreviewLoading(false);
            }
        },
        [scheduleId],
    );

    const canConfirm = Boolean(preview?.can_import && Array.isArray(preview.rows) && preview.rows.length > 0);

    const selectedKey = useMemo(() => {
        if (!selectedCandidate) return "";
        return `${selectedCandidate.draft?.id || ""}:${selectedCandidate.track?.id || ""}`;
    }, [selectedCandidate]);

    const columns = [
        { title: "航班号", dataIndex: "flight_number", width: 100 },
        { title: "飞机", dataIndex: "aircraft_reg", width: 110 },
        { title: "日期", dataIndex: "flight_date", width: 110 },
        { title: "出发", dataIndex: "departure_airport", width: 80 },
        { title: "到达", dataIndex: "arrival_airport", width: 80 },
        { title: "STD(UTC)", dataIndex: "std", width: 170 },
        { title: "STA(UTC)", dataIndex: "sta", width: 170 },
        { title: "航路", dataIndex: "flight_route_name", width: 140, render: (value, record) => value || record.flight_route_id || "-" },
    ];

    return (
        <Modal
            title="从 V2 编辑轨导入"
            open={open}
            onCancel={onCancel}
            width={980}
            destroyOnHidden
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    取消
                </Button>,
                <Button key="confirm" type="primary" disabled={!canConfirm} loading={previewLoading} onClick={() => onImportRows(preview.rows, preview)}>
                    确认导入
                </Button>,
            ]}
        >
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Alert message="仅导入 V2 后端已保存内容；V2 编辑页未保存的本地修改不会被读取。导入后只追加到当前 V1 编辑器，仍需手动保存和重新对比后才能发布。" type="info" showIcon />

                {targetSchedule && (
                    <Descriptions size="small" bordered column={3}>
                        <Descriptions.Item label="目标 V1 计划">{targetSchedule.name || targetSchedule.id}</Descriptions.Item>
                        <Descriptions.Item label="目标飞机">{targetSchedule.aircraft_reg || "未绑定"}</Descriptions.Item>
                        <Descriptions.Item label="状态">{targetSchedule.status || "-"}</Descriptions.Item>
                    </Descriptions>
                )}

                {error && <Alert type="error" showIcon message={error} />}

                {loading ? (
                    <div style={{ textAlign: "center", padding: 32 }}>
                        <Spin />
                        <div style={{ marginTop: 8 }}>正在加载 V2 编辑轨来源...</div>
                    </div>
                ) : candidates.length > 0 ? (
                    <List
                        size="small"
                        bordered
                        dataSource={candidates}
                        renderItem={(candidate) => {
                            const itemKey = `${candidate.draft?.id || ""}:${candidate.track?.id || ""}`;
                            const selected = itemKey === selectedKey;
                            const blockingText = formatBlockingReasons(candidate.blocking_reasons);
                            return (
                                <List.Item
                                    actions={[
                                        <Button key="select" type={selected ? "primary" : "default"} size="small" loading={selected && previewLoading} aria-label={selected ? "已选择" : "选择"} onClick={() => handleSelectCandidate(candidate)}>
                                            {selected ? "已选择" : "选择"}
                                        </Button>,
                                    ]}
                                >
                                    <List.Item.Meta
                                        title={
                                            <Space wrap>
                                                <Text strong>{candidate.draft?.name || candidate.draft?.id}</Text>
                                                <Text type="secondary">/</Text>
                                                <Text>{candidate.track?.name || candidate.track?.id}</Text>
                                                <Tag color={candidate.track?.is_editable ? "green" : "default"}>{candidate.track?.is_editable ? "可编辑" : "只读快照"}</Tag>
                                                <Tag>{candidate.track?.track_type || "-"}</Tag>
                                                {!candidate.can_import && <Tag color="red">不可导入</Tag>}
                                            </Space>
                                        }
                                        description={
                                            <Space direction="vertical" size={2}>
                                                <Text type="secondary">
                                                    周期 {candidate.draft?.period_start || "?"} ~ {candidate.draft?.period_end || "?"}，状态 {candidate.draft?.status || "-"}
                                                </Text>
                                                <Text type="secondary">
                                                    航班 {candidate.flight_count || 0} 个，飞机 {formatAircraftRegs(candidate.aircraft_regs)}，日期 {formatDateRange(candidate.date_range)}
                                                </Text>
                                                {blockingText && <Text type="danger">{blockingText}</Text>}
                                            </Space>
                                        }
                                    />
                                </List.Item>
                            );
                        }}
                    />
                ) : (
                    !error && <Empty description="没有可读取的 V2 EDITING 轨来源" />
                )}

                {previewError && <Alert type="error" showIcon message={previewError} />}

                {preview && (
                    <Space direction="vertical" size={12} style={{ width: "100%" }}>
                        <Descriptions size="small" bordered column={4}>
                            <Descriptions.Item label="来源航班">{preview.source?.flight_count || 0}</Descriptions.Item>
                            <Descriptions.Item label="来源飞机">{formatAircraftRegs(preview.source?.aircraft_regs)}</Descriptions.Item>
                            <Descriptions.Item label="来源日期">{formatDateRange(preview.source?.date_range)}</Descriptions.Item>
                            <Descriptions.Item label="可导入">{preview.can_import ? "是" : "否"}</Descriptions.Item>
                        </Descriptions>

                        {preview.blocking_reasons?.length > 0 && <Alert type="error" showIcon message="该 V2 编辑轨不能导入" description={formatBlockingReasons(preview.blocking_reasons)} />}

                        {preview.can_import && (
                            <Table
                                size="small"
                                rowKey={(record) => record.source_v2_flight_id || `${record.flight_number}-${record.std}`}
                                dataSource={preview.rows || []}
                                columns={columns}
                                pagination={{ pageSize: 20, size: "small" }}
                                scroll={{ x: 980, y: 260 }}
                            />
                        )}
                    </Space>
                )}
            </Space>
        </Modal>
    );
};

export default V2TrackImportModal;
export { extractApiErrorMessage, formatAircraftRegs, formatBlockingReasons, formatDateRange };
